import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { REFERRALAPIURL } from '../config';

const parsePlaceDetails = (details) => {
  const comps = details?.result?.address_components || [];
  const find = (type) => comps.find((c) => c.types.includes(type));
  const long = (t) => find(t)?.long_name || '';
  const short = (t) => find(t)?.short_name || '';
  const streetNumber = long('street_number');
  const route = long('route');
  const city = long('locality') || long('sublocality') || long('postal_town');
  const state = short('administrative_area_level_1');
  const zip = long('postal_code');
  const address_line_1 = [streetNumber, route].filter(Boolean).join(' ');
  const latitude = details?.result?.geometry?.location?.lat;
  const longitude = details?.result?.geometry?.location?.lng;
  return { address_line_1, address_line_2: '', city, state, zip, latitude, longitude };
};

/**
 * ReferralInfoSection  (Section 2 on the portal)
 * Contextual pre-form fields collected before the modal opens.
 *
 * mode / hasDynamicForm → what to show:
 *   null | 'standard'               → nothing  (OBForm has its own fields)
 *   'company'|'external', no schema → just "Referring Org" text field
 *   'company', has schema           → "Referring Org" + "Client Name" + "Client Address"
 *
 * Props:
 *   mode                     – null | 'standard' | 'company' | 'external'
 *   hasDynamicForm           – bool
 *   value                    – { initiatingCompanyName, clientName, address }
 *   onChange                 – (name: string, value: string) => void
 *   onPatientAddressResolved – (canonicalObj | null) => void
 */
const ReferralInfoSection = ({ mode, hasDynamicForm, value, onChange, onPatientAddressResolved, showErrors = false }) => {
  const v = value || {};

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const fetchSuggestions = useCallback((input) => {
    if (!input || input.trim().length < 3) { setAddressSuggestions([]); return; }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setAddrLoading(true);
    axios
      .get(`${REFERRALAPIURL}/maps`, { params: { input }, signal: controller.signal })
      .then((resp) => { if (!controller.signal.aborted) setAddressSuggestions(resp.data?.predictions || []); })
      .catch(() => { if (!controller.signal.aborted) setAddressSuggestions([]); })
      .finally(() => { if (!controller.signal.aborted) setAddrLoading(false); });
  }, []);

  const debouncedFetch = useCallback((val) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 400);
  }, [fetchSuggestions]);

  const handleAddressSelect = useCallback(async (prediction) => {
    if (!prediction || typeof prediction !== 'object') { onPatientAddressResolved?.(null); return; }
    try {
      const resp = await axios.get(`${REFERRALAPIURL}/maps/details`, { params: { place_id: prediction.place_id } });
      const parsed = parsePlaceDetails(resp.data);
      onPatientAddressResolved?.(parsed);
      onChange('address', prediction.description || parsed.address_line_1);
    } catch (_) { /* ignore */ }
  }, [onChange, onPatientAddressResolved]);

  // Nothing to collect for standard form – OBForm includes all fields itself
  if (!mode || mode === 'standard') return null;

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <TextField
          fullWidth
          size="small"
          required
          label="Referring Company / Organization"
          placeholder="Your organization name"
          value={v.initiatingCompanyName || ''}
          onChange={(e) => onChange('initiatingCompanyName', e.target.value)}
          error={showErrors && !(v.initiatingCompanyName || '').trim()}
          helperText={showErrors && !(v.initiatingCompanyName || '').trim() ? 'Required' : ''}
        />
      </Grid>

      {hasDynamicForm && (
        <>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              required
              label="Client Name"
              placeholder="Client / patient this referral is about"
              value={v.clientName || ''}
              onChange={(e) => onChange('clientName', e.target.value)}
              error={showErrors && !(v.clientName || '').trim()}
              helperText={showErrors && !(v.clientName || '').trim() ? 'Required' : ''}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete
              fullWidth
              filterOptions={(x) => x}
              options={addressSuggestions}
              loading={addrLoading}
              autoComplete
              includeInputInList
              freeSolo
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.description || '')}
              inputValue={v.address || ''}
              onChange={(e, newVal) => {
                if (newVal && typeof newVal === 'object' && newVal.place_id) {
                  handleAddressSelect(newVal);
                } else {
                  onPatientAddressResolved?.(null);
                }
              }}
              onInputChange={(e, newInput, reason) => {
                if (reason === 'input') {
                  onChange('address', newInput);
                  onPatientAddressResolved?.(null);
                  debouncedFetch(newInput);
                } else if (reason === 'clear') {
                  onChange('address', '');
                  onPatientAddressResolved?.(null);
                  setAddressSuggestions([]);
                }
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.place_id || option.description} style={{ fontSize: 14 }}>
                  {option.description || option}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  required
                  label="Client Address"
                  placeholder="Start typing to search"
                  error={showErrors && !(v.address || '').trim()}
                  helperText={showErrors && !(v.address || '').trim() ? 'Required' : ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {addrLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default ReferralInfoSection;
